import { useEffect, useMemo } from 'react';
import { Create, useForm } from '@refinedev/antd';
import { useList, HttpError } from '@refinedev/core';
import { Form, Input, Select, Alert } from 'antd';
import type { Conference, Role } from '@viq-admin/types';
import { ROLE_OPTIONS, normalizeEmailInput, space } from '@viq-admin/ui';
import { RequireCan } from '../../components/RequireCan';
import { useCompanyScope } from '../../components/CompanyScopePicker';

/**
 * Invite (create) a new user into a specific tenant. Unlike edit, this form
 * DOES carry a `companyId` — the super-admin picks which company the user
 * belongs to (from the cross-tenant `useCompanyScope().companies` list); once
 * set, `companyId` is immutable. Role options exclude `super_admin`
 * (`ROLE_OPTIONS`). Conferences are optional and assigned immediately.
 *
 * The Company field is PREFILLED (not locked) from the "Operate as tenant"
 * scope picker (`useCompanyScope().companyId`) when one is active — the
 * common case is inviting a user into the tenant you're currently operating
 * as. It stays editable so a super-admin can still target a different
 * company; when scope is "All tenants" (`companyId` is null) the field
 * starts blank as before.
 *
 * The form field names match the API body exactly, so Refine's `useForm`
 * forwards `{ name, email, companyId, role, conferenceIds }` to the
 * dataProvider `create` without an `onFinish` remap. `manage User` gates the
 * whole page.
 *
 * Refine v6 `useList` returns `{ query, result }`; conference options read
 * `result.data`.
 */
interface InviteUserInput {
  name: string;
  email: string;
  companyId: string;
  role: Role;
  conferenceIds: string[];
}

export function UserCreate() {
  const { formProps, saveButtonProps, form } = useForm<
    InviteUserInput,
    HttpError,
    InviteUserInput
  >({ resource: 'users', action: 'create' });
  const { companies, companyId: scopedCompanyId } = useCompanyScope();
  // A user may only be assigned to conferences their company has been granted,
  // so list the SELECTED company's conferences. The dataProvider maps
  // `meta.companyId` to `?companyId=` (Refine `filters` are ignored there).
  const selectedCompanyId = Form.useWatch('companyId', form) as
    | string
    | undefined;
  const { result, query } = useList<Conference>({
    resource: 'conferences',
    meta: { companyId: selectedCompanyId ?? null },
    queryOptions: { enabled: !!selectedCompanyId },
  });
  const grantedConferences = useMemo(
    () => (selectedCompanyId ? (result?.data ?? []) : []),
    [selectedCompanyId, result],
  );

  // When the company changes, drop selections the new company isn't granted.
  const settled = !!selectedCompanyId && !query?.isFetching;
  useEffect(() => {
    if (!settled) return;
    const current = (form.getFieldValue('conferenceIds') ?? []) as string[];
    const grantedIds = new Set(grantedConferences.map((c) => c.id));
    const kept = current.filter((id) => grantedIds.has(id));
    if (kept.length !== current.length) {
      form.setFieldsValue({ conferenceIds: kept });
    }
  }, [settled, grantedConferences, form]);

  useEffect(() => {
    if (scopedCompanyId) form.setFieldsValue({ companyId: scopedCompanyId });
  }, [scopedCompanyId, form]);

  const conferenceOptions = useMemo(
    () =>
      grantedConferences.map((c) => ({
        label: c.location ? `${c.name} — ${c.location}` : c.name,
        value: c.id,
      })),
    [grantedConferences],
  );
  const companyOptions = useMemo(
    () => companies.map((c) => ({ label: c.name, value: c.id })),
    [companies],
  );

  return (
    <RequireCan I="manage" a="User">
      <Create saveButtonProps={saveButtonProps}>
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: space.lg }}
          message="Invite a new user to a specific company."
        />
        <Form {...formProps} layout="vertical">
          <Form.Item
            label="Name"
            name="name"
            rules={[{ required: true, message: 'Name is required' }]}
          >
            <Input maxLength={200} />
          </Form.Item>
          <Form.Item
            label="Email"
            name="email"
            normalize={normalizeEmailInput}
            rules={[
              { required: true, message: 'Email is required' },
              { type: 'email', message: 'Enter a valid email' },
            ]}
          >
            <Input type="email" maxLength={200} />
          </Form.Item>
          <Form.Item
            label="Company"
            name="companyId"
            rules={[{ required: true, message: 'Company is required' }]}
          >
            <Select
              placeholder="Select the tenant for this user"
              options={companyOptions}
              showSearch
              filterOption={(input, option) =>
                ((option?.label as string) ?? '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            />
          </Form.Item>
          <Form.Item
            label="Role"
            name="role"
            rules={[{ required: true, message: 'Role is required' }]}
          >
            <Select placeholder="Select a role" options={ROLE_OPTIONS} />
          </Form.Item>
          <Form.Item label="Conferences" name="conferenceIds">
            <Select
              mode="multiple"
              placeholder={
                selectedCompanyId
                  ? 'Optional: assign to conferences immediately'
                  : 'Select a company first'
              }
              disabled={!selectedCompanyId}
              options={conferenceOptions}
              allowClear
            />
          </Form.Item>
        </Form>
      </Create>
    </RequireCan>
  );
}
